import { useEffect, useState } from "react";
import { api, mediaUrl, asArray } from "@/lib/api";
import { useTick } from "@/context/LiveContext";
import { Link, useParams } from "react-router-dom";
import { Divider } from "@/components/Ornaments";
import { Newsletter } from "@/pages/Home";
import { ArrowLeft, ArrowRight, Clock, Tag, User, ChevronLeft, ChevronRight } from "lucide-react";

const LIMIT = 6;

/* Defensive helpers: a single odd blog record must never break the page */
const list = (x) => (Array.isArray(x) ? x.filter((t) => t != null && t !== "") : typeof x === "string" && x ? x.split(",").map((t) => t.trim()).filter(Boolean) : []);
const fmtDate = (d, opts) => { try { const t = new Date(d); return isNaN(t) ? "" : t.toLocaleDateString("en-IN", opts); } catch { return ""; } };
const norm = (b) => (b && typeof b === "object" ? { ...b, tags: list(b.tags), sub_tags: list(b.sub_tags), images: list(b.images), sections: Array.isArray(b.sections) ? b.sections.filter((x) => x && typeof x === "object") : [] } : null);

export default function Blogs() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [tag, setTag] = useState("");
  const [loading, setLoading] = useState(true);
  const tick = useTick();
  const [allTags, setAllTags] = useState([]);
  useEffect(()=>{ if (!tick) setLoading(true); api.get("/blogs", { params: { page, limit: LIMIT, tag: tag || undefined } }).then(r=>{
    // older servers return a plain list
    const raw = Array.isArray(r.data) ? r.data : asArray(r.data?.items);
    setData({ items: raw.map(norm).filter(Boolean), total: Array.isArray(r.data) ? raw.length : Number(r.data?.total) || 0 });
  }).catch(()=>{}).finally(()=>setLoading(false)); }, [page, tag, tick]);
  useEffect(()=>{ api.get("/blogs/tags").then(r=>setAllTags(asArray(r.data).map((t)=>t?.tag).filter(Boolean))).catch(()=>{}); }, [tick]);
  useEffect(()=>{ window.scrollTo({ top: 0, behavior: "smooth" }); }, [page, tag]);
  const items = asArray(data.items);
  const pages = Math.max(1, Math.ceil((data.total || 0) / LIMIT));
  const [hero, ...rest] = items;
  const tags = allTags.length ? allTags : Array.from(new Set(items.flatMap(b=>b.tags)));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-12 md:py-16">
      <div className="text-center max-w-2xl mx-auto">
        <Divider label="Journal" center/>
        <h1 className="font-display text-4xl md:text-6xl text-[color:var(--maroon)] mt-3">Stories, checklists & inspiration</h1>
        <p className="text-neutral-600 mt-3">Field notes from weddings across India — written for families who want to get it right the first time.</p>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap justify-center gap-2 mt-8" data-testid="blog-tags">
          <button onClick={()=>{ setTag(""); setPage(1); }} className={`px-4 py-1.5 rounded-full text-xs font-semibold border ${!tag ? "bg-[color:var(--maroon)] text-[color:var(--cream-primary)] border-[color:var(--maroon)]" : "border-[color:var(--gold)]/50 text-[color:var(--maroon)]"}`}>All</button>
          {tags.map(t=><button key={t} onClick={()=>{ setTag(t); setPage(1); }} className={`px-4 py-1.5 rounded-full text-xs font-semibold border ${tag===t ? "bg-[color:var(--maroon)] text-[color:var(--cream-primary)] border-[color:var(--maroon)]" : "border-[color:var(--gold)]/50 text-[color:var(--maroon)]"}`}>{t}</button>)}
        </div>
      )}

      {loading && <div className="grid md:grid-cols-3 gap-6 mt-12">{Array.from({length:3}).map((_,i)=><div key={i} className="aspect-[4/3] skeleton"/>)}</div>}

      {!loading && hero && page === 1 && !tag && (
        <Link to={`/blogs/${hero.slug}`} className="mt-12 grid lg:grid-cols-2 gap-8 card-heritage p-4 md:p-6 group block" data-testid="blog-hero">
          <div className="rounded-[4px] overflow-hidden aspect-[16/11]"><img src={mediaUrl(hero.cover_image)} alt={hero.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/></div>
          <div className="flex flex-col justify-center p-2 md:p-6">
            <Meta b={hero}/>
            <h2 className="font-display text-3xl md:text-5xl text-[color:var(--maroon)] leading-tight mt-3">{hero.title}</h2>
            <p className="text-neutral-700 mt-4 leading-relaxed">{hero.excerpt}</p>
            <span className="mt-6 text-[color:var(--maroon)] font-semibold inline-flex items-center gap-1">Read the story <ArrowRight className="w-4 h-4"/></span>
          </div>
        </Link>
      )}

      {!loading && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-10" data-testid="blog-grid">
          {(page === 1 && !tag ? rest : items).map(p=>(
            <Link to={`/blogs/${p.slug}`} key={p.id} className="card-luxury overflow-hidden group block" data-testid={`blog-card-${p.slug}`}>
              <div className="aspect-[16/10] overflow-hidden"><img src={mediaUrl(p.cover_image)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={p.title}/></div>
              <div className="p-6">
                <Meta b={p}/>
                <h3 className="font-display text-2xl leading-tight text-[color:var(--maroon)] mt-2">{p.title}</h3>
                <p className="text-sm text-neutral-600 mt-2 line-clamp-3 leading-relaxed">{p.excerpt}</p>
              </div>
            </Link>
          ))}
          {!items.length && <div className="col-span-full text-center text-neutral-500 py-20">No stories yet — check back soon.</div>}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-12" data-testid="blog-pagination">
          <button disabled={page===1} onClick={()=>setPage(page-1)} className="w-10 h-10 rounded-full border border-[color:var(--maroon)]/40 flex items-center justify-center disabled:opacity-30" data-testid="blog-prev"><ChevronLeft className="w-4 h-4"/></button>
          {Array.from({length: pages}).map((_, i)=>(
            <button key={i} onClick={()=>setPage(i+1)} className={`w-10 h-10 rounded-full text-sm font-semibold ${page===i+1 ? "bg-[color:var(--maroon)] text-[color:var(--cream-primary)]" : "border border-[color:var(--gold)]/50 text-[color:var(--maroon)]"}`} data-testid={`blog-page-${i+1}`}>{i+1}</button>
          ))}
          <button disabled={page===pages} onClick={()=>setPage(page+1)} className="w-10 h-10 rounded-full border border-[color:var(--maroon)]/40 flex items-center justify-center disabled:opacity-30" data-testid="blog-next"><ChevronRight className="w-4 h-4"/></button>
        </div>
      )}
    </div>
  );
}

function Meta({ b }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] uppercase tracking-widest-2 text-[color:var(--gold)]">
      {b.tags?.[0] && <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3"/> {b.tags[0]}</span>}
      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3"/> {b.read_minutes || 4} min</span>
      {fmtDate(b.created_at, { day: "numeric", month: "short", year: "numeric" }) && <span>{fmtDate(b.created_at, { day: "numeric", month: "short", year: "numeric" })}</span>}
    </div>
  );
}

export function BlogDetail() {
  const { slug } = useParams();
  const [b, setB] = useState(null);
  const [more, setMore] = useState([]);
  useEffect(()=>{ window.scrollTo(0,0); api.get(`/blogs/${slug}`).then(r=>setB(norm(r.data) || false)).catch(()=>setB(false)); api.get("/blogs", { params: { limit: 3 } }).then(r=>setMore(asArray(r.data?.items).map(norm).filter((x)=>x && x.slug!==slug).slice(0,2))).catch(()=>{}); }, [slug]);
  if (b === null) return <div className="max-w-3xl mx-auto px-6 py-20"><div className="h-10 w-2/3 skeleton mb-6"/><div className="aspect-video skeleton"/></div>;
  if (b === false) return <div className="text-center py-32 text-neutral-500">Story not found. <Link to="/blogs" className="underline text-[color:var(--maroon)]">Back to journal</Link></div>;
  return (
    <article className="pb-20" data-testid="blog-detail">
      <div className="relative h-[52vh] min-h-[360px] overflow-hidden">
        <img src={mediaUrl(b.cover_image)} alt={b.title} className="w-full h-full object-cover"/>
        <div className="absolute inset-0 hero-overlay"/>
        <div className="absolute inset-x-0 bottom-0 max-w-3xl mx-auto px-6 pb-12 text-white">
          <Link to="/blogs" className="inline-flex items-center gap-1 text-xs text-[color:var(--gold-2)] hover:underline mb-4"><ArrowLeft className="w-3.5 h-3.5"/> Journal</Link>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05]">{b.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-white/80">
            <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5"/> {b.author || "Team DartMyVenue"}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5"/> {b.read_minutes || 4} min read</span>
            <span>{fmtDate(b.created_at, { day: "numeric", month: "long", year: "numeric" })}</span>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 mt-12">
        <p className="font-display text-2xl md:text-3xl text-[color:var(--maroon)] leading-snug">{b.excerpt}</p>
        <div className="mt-8 text-[17px] leading-[1.85] text-[color:var(--ink)] whitespace-pre-line">{b.content}</div>
        {b.sections.map((s, i)=>(
          <section key={i} className="mt-12" data-testid={`blog-section-${i}`}>
            {s.heading && <h2 className="font-display text-3xl md:text-4xl text-[color:var(--maroon)] leading-tight">{s.heading}</h2>}
            {s.body && <p className="mt-4 text-[17px] leading-[1.85] whitespace-pre-line">{s.body}</p>}
            {s.image && <figure className="mt-6"><img src={mediaUrl(s.image)} alt={s.caption || ""} className="w-full rounded-2xl border border-[color:var(--line)]"/>{s.caption && <figcaption className="text-center text-xs text-neutral-500 mt-2 italic">{s.caption}</figcaption>}</figure>}
          </section>
        ))}
        {b.images.length > 1 && (
          <div className="grid grid-cols-2 gap-3 mt-12">{b.images.slice(0, 4).map((img, i)=><img key={i} src={mediaUrl(img)} alt="" className={`rounded-2xl object-cover w-full ${i===0 && b.images.length%2 ? "col-span-2 aspect-[16/9]" : "aspect-[4/3]"}`}/>)}</div>
        )}
        <div className="mt-12 flex flex-wrap gap-2">
          {b.tags.map(t=><span key={t} className="px-3 py-1 rounded-full bg-[color:var(--maroon)] text-[color:var(--cream-primary)] text-xs font-semibold">{t}</span>)}
          {b.sub_tags.map(t=><span key={t} className="px-3 py-1 rounded-full border border-[color:var(--gold)]/60 text-[color:var(--maroon)] text-xs">#{t}</span>)}
        </div>
      </div>
      {more.length > 0 && (
        <div className="max-w-5xl mx-auto px-6 mt-20">
          <Divider label="Keep reading"/>
          <div className="grid sm:grid-cols-2 gap-6 mt-6">
            {more.map(p=>(
              <Link to={`/blogs/${p.slug}`} key={p.id} className="card-luxury overflow-hidden group flex">
                <img src={mediaUrl(p.cover_image)} alt="" className="w-36 object-cover"/>
                <div className="p-5"><Meta b={p}/><div className="font-display text-xl text-[color:var(--maroon)] mt-1 leading-tight">{p.title}</div></div>
              </Link>
            ))}
          </div>
        </div>
      )}
      <div className="max-w-5xl mx-auto px-6 mt-16"><Newsletter compact/></div>
    </article>
  );
}
